import React, { useState, useEffect } from 'react';
import { Link, useParams, useLocation } from 'react-router-dom';
import { Mail, ChevronRight, Filter, Wrench, Package, ChevronDown, ChevronUp, ArrowRight, Truck, Shield, CheckCircle, ShoppingCart } from 'lucide-react';
import DOMPurify from 'dompurify';
import SEO from '../components/SEO';
import ContactCTA from '../components/ContactCTA';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// =============================================
// TEMPLATE SEO UNIVERSEL - CONFIGURATION CATÉGORIES
// Format H1 obligatoire: "[Nom catégorie] pour spa – Livraison France"
// =============================================
const CATEGORY_SEO_CONFIG = {
  'pompes-circulation': {
    title: 'Pompes de circulation',
    h1: 'Pompes de circulation pour spa – Livraison France',
    metaTitle: 'Pompes de circulation pour spa | Livraison France | Atlantique Spa Boutique',
    metaDescription: 'Large sélection de pompes de circulation pour spa. Livraison rapide partout en France. Compatibilité vérifiée avant expédition.',
    icon: '🔄',
    brands: ['LX Whirlpool', 'Laing', 'Waterway'],
    // Bloc introductif (au-dessus du listing)
    intro: `Nous proposons une sélection de pompes de circulation pour spa, compatibles avec les principales marques et modèles (LX Whirlpool, Laing, Waterway). Livraison rapide partout en France. Bien vérifier les dimensions et références avant commande.`,
    // Texte SEO long (100-200 mots) - sous le listing
    seoText: `La pompe de circulation est le cœur de votre système de filtration spa. Elle assure un brassage continu de l'eau, permettant une filtration efficace et une répartition homogène des produits de traitement. Une pompe défaillante entraîne une eau trouble et une surconsommation des produits chimiques.

**Comment choisir votre pompe de circulation ?** Le débit (exprimé en L/min) doit correspondre à la capacité de votre spa. Les modèles JA50, JA75 et JA100 de LX Whirlpool couvrent 90% des spas du marché. Vérifiez également le diamètre des raccords (généralement 1.5" ou 2") et la tension (230V monophasé).

**Marques compatibles** : Nos pompes sont compatibles avec les spas Sundance, Jacuzzi, HotSpring, Caldera, Master Spas, Dimension One, et la plupart des marques chinoises.

📦 **Livraison France métropolitaine** sous 48-72h selon stock.`,
    // Catégories sœurs pour le maillage interne
    relatedCategories: ['pompes-massage', 'blower'],
    internalLinks: ['/remplacement-pompe-spa-nantes', '/pieces-accessoires']
  },
  'pompes-massage': {
    title: 'Pompes de massage',
    h1: 'Pompes de massage pour spa – Livraison France',
    metaTitle: 'Pompes de massage pour spa | Livraison France | Atlantique Spa Boutique',
    metaDescription: 'Large sélection de pompes de massage mono et bi-vitesse pour spa. Livraison rapide partout en France. Bien vérifier les références avant commande.',
    icon: '💪',
    brands: ['LX Whirlpool', 'Waterway', 'Gecko/Aqua-Flo'],
    intro: `Nous proposons une sélection de pompes de massage pour spa, mono-vitesse et bi-vitesse, compatibles avec les principales marques. Livraison rapide partout en France. Bien vérifier les dimensions et références avant commande.`,
    seoText: `La pompe de massage (ou pompe jets) crée la puissance hydraulique qui alimente vos jets de massage. C'est l'élément clé du confort de votre spa. Une pompe sous-dimensionnée donnera des jets faibles, une pompe surdimensionnée consommera inutilement de l'énergie.

**Mono-vitesse ou bi-vitesse ?** Les pompes mono-vitesse offrent une puissance constante et sont plus économiques à l'achat. Les pompes bi-vitesse permettent deux modes de fonctionnement : basse vitesse pour une relaxation douce, haute vitesse pour un massage tonique. Elles consomment moins d'électricité en mode doux.

**Critères de choix** : Puissance (de 1.5 HP à 5 HP selon le nombre de jets), diamètre des raccords (48mm frame ou 56mm frame), sens de rotation, tension électrique.

**Marques compatibles** : LX Whirlpool, Waterway Executive, Gecko Aqua-Flo, et équivalents pour Sundance, Jacuzzi, HotSpring, Caldera.

📦 **Livraison France** sous 48-72h.`,
    relatedCategories: ['pompes-circulation', 'blower'],
    internalLinks: ['/remplacement-pompe-spa-nantes', '/pieces-accessoires']
  },
  'blower': {
    title: 'Blower / Soufflerie',
    h1: 'Blower pour spa – Livraison France',
    metaTitle: 'Blower pour spa | Livraison France | Atlantique Spa Boutique',
    metaDescription: 'Large sélection de blowers et souffleries pour spa. Livraison rapide partout en France. Bien vérifier les références avant commande.',
    icon: '💨',
    brands: ['LX Whirlpool', 'Balboa', 'Gecko'],
    intro: `Nous proposons une sélection de blowers (souffleries) pour spa, différentes puissances disponibles. Livraison rapide partout en France. Bien vérifier les dimensions et références avant commande.`,
    seoText: `Le blower (ou soufflerie) injecte de l'air dans l'eau de votre spa pour créer des milliers de bulles de massage. Il complète l'action des jets hydrauliques pour une relaxation encore plus intense. Un blower silencieux et puissant améliore considérablement l'expérience spa.

**Comment choisir la puissance ?** Elle dépend du nombre de buses à air de votre spa. Un blower 400W convient pour 6-10 buses, 700W pour 10-20 buses, 900W+ pour les spas avec système air-jets étendu. Un blower sous-dimensionné produira des bulles faibles.

**Critères techniques** : Puissance (400W à 1500W), tension (230V), diamètre de sortie d'air (généralement 32mm ou 50mm), avec ou sans réchauffeur intégré (évite l'air froid).

**Marques compatibles** : LX Whirlpool APR, Balboa, Gecko. Compatible avec la plupart des spas du marché.

📦 **Livraison France métropolitaine** 48-72h.`,
    relatedCategories: ['pompes-circulation', 'pompes-massage'],
    internalLinks: ['/pieces-accessoires']
  },
  'filtres': {
    title: 'Filtres et cartouches',
    h1: 'Filtres pour spa – Livraison France',
    metaTitle: 'Filtres pour spa | Livraison France | Atlantique Spa Boutique',
    metaDescription: 'Large sélection de filtres et cartouches pour spa. Livraison rapide partout en France. Bien vérifier les dimensions avant commande.',
    icon: '🔍',
    brands: ['Darlly', 'Unicel', 'Pleatco', 'Magnum'],
    intro: `Nous proposons une sélection de filtres et cartouches pour spa, compatibles avec toutes les marques. Livraison rapide partout en France. Bien vérifier les dimensions avant commande.`,
    seoText: `La cartouche filtrante est essentielle pour maintenir une eau cristalline dans votre spa. Elle retient les impuretés, cheveux, résidus de produits et micro-organismes. Un filtre encrassé ou usé réduit l'efficacité de filtration et peut endommager votre pompe de circulation.

**Comment choisir le bon filtre ?** Mesurez votre cartouche actuelle : diamètre extérieur, longueur totale, et diamètre du trou central. Ces trois dimensions déterminent la compatibilité. Envoyez-nous une photo avec les mesures pour confirmation.

**Fréquence de remplacement** : Nettoyez votre filtre toutes les 2 semaines à l'eau claire. Effectuez un nettoyage chimique mensuel avec un produit dégraissant. Remplacez la cartouche tous les 12-18 mois selon l'utilisation.

**Marques compatibles** : Darlly, Unicel, Pleatco. Nos filtres génériques offrent la même qualité de filtration que les originaux Sundance, Jacuzzi, HotSpring, Caldera.

📦 **Livraison France** 48-72h.
🛠 **Conseil personnalisé** pour trouver le bon modèle.`,
    relatedCategories: ['produits-traitement'],
    internalLinks: ['/entretien-spa', '/pieces-accessoires']
  },
  'claviers-commandes': {
    title: 'Claviers & Commandes',
    h1: 'Claviers de commande pour spa – Livraison France',
    metaTitle: 'Claviers de commande tactile pour spa | Balboa Spa Touch | Livraison France',
    metaDescription: 'Claviers de commande et panneaux tactiles pour spa. Balboa Spa Touch 2, 3, 4 WiFi. Livraison rapide partout en France.',
    icon: '🎛️',
    brands: ['Balboa', 'Gecko', 'Sundance'],
    intro: `Nous proposons une sélection de claviers de commande et panneaux tactiles pour spa : Balboa Spa Touch, TP600, TP700 et compatibles. Livraison rapide partout en France. Bien vérifier les références avant commande.`,
    seoText: `Le clavier de commande est l'interface entre vous et votre spa. Il permet de contrôler la température, les pompes, l'éclairage et les cycles de filtration. Un clavier défaillant peut bloquer totalement l'utilisation de votre spa.

**Les gammes Balboa Spa Touch** : La série Spa Touch offre des écrans tactiles couleur intuitifs. Le Spa Touch 2 est un modèle d'entrée de gamme, le Spa Touch 3 ajoute des fonctionnalités multimédia, et le Spa Touch 4 intègre le WiFi pour un contrôle à distance via smartphone.

**Claviers à boutons TP600/TP700** : Pour ceux qui préfèrent les touches physiques, les modèles TP600 (compact) et TP700 (allongé) offrent fiabilité et simplicité d'utilisation avec afficheur LCD.

**Compatibilité** : Les claviers Balboa sont compatibles avec les systèmes BP (BP501, BP601, BP2100, BP8). Vérifiez le modèle de votre boîtier de commande avant achat.

📦 **Livraison France métropolitaine** 48-72h.`,
    relatedCategories: ['boitiers-commande', 'rechauffeurs'],
    internalLinks: ['/pieces-accessoires']
  },
  'rechauffeurs': {
    title: 'Réchauffeurs',
    h1: 'Réchauffeurs pour spa – Livraison France',
    metaTitle: 'Réchauffeurs pour spa | Balboa, Gecko | Livraison France | Atlantique Spa Boutique',
    metaDescription: 'Réchauffeurs et éléments chauffants pour spa. Balboa M7, GS100, Gecko, Ethink. Livraison rapide partout en France.',
    icon: '🔥',
    brands: ['Balboa', 'Gecko', 'Ethink'],
    intro: `Nous proposons une sélection de réchauffeurs, boîtiers de contrôle et claviers électroniques pour spa : Balboa M7, GS100, Gecko IN.K, Ethink et compatibles. Livraison rapide partout en France. Bien vérifier les références avant commande.`,
    seoText: `Le réchauffeur (ou élément chauffant) est la pièce qui chauffe l'eau de votre spa. C'est un composant critique : un réchauffeur défaillant vous prive de l'usage de votre spa jusqu'à son remplacement.

**Symptômes d'un réchauffeur défaillant** : L'eau ne chauffe plus ou met très longtemps à atteindre la température, le disjoncteur saute lors du chauffage, message d'erreur sur le clavier (OH, HL, FLO).

**Comment choisir ?** La puissance (2kW, 3kW, 4kW) doit correspondre à votre système d'origine. Les raccords (généralement 2" Union) doivent être compatibles. Les modèles H30, Balboa M7 et GS100 équipent la majorité des spas.

**Boîtiers de contrôle** : Le Balboa GS100 est une solution économique pour les spas 1 pompe. Il gère pompe mono/bi-vitesse, ozonateur, éclairage et audio.

**Claviers de commande** : Les modèles Ethink KL8 et Gecko IN.K offrent des interfaces modernes avec écran LCD.

📦 **Livraison France** 48-72h.`,
    relatedCategories: ['boitiers-commande', 'claviers-commandes'],
    internalLinks: ['/pieces-accessoires']
  },
  'boitiers-commande': {
    title: 'Boîtiers de commande',
    h1: 'Boîtiers de commande (Spa Pack) – Livraison France',
    metaTitle: 'Boîtiers de commande Balboa BP | Spa Pack | Livraison France',
    metaDescription: 'Boîtiers de commande Spa Pack pour spa. Balboa BP501, BP601, BP8. Livraison rapide partout en France.',
    icon: '📦',
    brands: ['Balboa', 'Gecko', 'Sundance'],
    intro: `Nous proposons une sélection de boîtiers de commande (Spa Pack) pour spa : Balboa BP501, BP601, BP8 et compatibles. Livraison rapide partout en France. Bien vérifier les références avant commande.`,
    seoText: `Le boîtier de commande (ou Spa Pack) est le "cerveau" de votre spa. Il gère l'ensemble des équipements : pompes, réchauffeur, blower, ozonateur, éclairage, et reçoit les ordres du clavier de commande.

**Quand remplacer le boîtier ?** En cas de dysfonctionnements multiples non résolus par le remplacement des composants individuels, d'erreurs système récurrentes, ou de carte électronique endommagée par l'humidité ou une surtension.

**La gamme Balboa BP** : BP501 (entrée de gamme, 1-2 pompes), BP601 (milieu de gamme, 2-3 pompes), BP2100/BP8 (haut de gamme, jusqu'à 4 pompes et fonctions avancées).

**Important** : Le remplacement d'un Spa Pack nécessite des compétences en électricité et la reprogrammation du système.

📦 **Livraison France** 48-72h.`,
    relatedCategories: ['claviers-commandes', 'rechauffeurs'],
    internalLinks: ['/pieces-accessoires']
  },
  'produits-traitement': {
    title: 'Produits de traitement',
    h1: 'Produits d\'entretien pour spa – Livraison France',
    metaTitle: 'Produits d\'entretien pour spa | Livraison France | Atlantique Spa Boutique',
    metaDescription: 'Large sélection de produits d\'entretien et traitement pour spa. Livraison rapide partout en France. Conseils personnalisés.',
    icon: '🧪',
    brands: ['HTH', 'Bayrol', 'AquaFinesse', 'SpaTime'],
    intro: `Nous proposons une sélection de produits d'entretien et de traitement pour spa : chlore, brome, oxygène actif, anti-calcaire, nettoyants. Livraison rapide partout en France. Besoin d'aide pour choisir ? Notre équipe vous conseille sur le traitement adapté à votre spa.`,
    seoText: `L'entretien de l'eau est fondamental pour profiter de votre spa en toute sécurité. Une eau mal traitée devient rapidement trouble, malodorante et peut provoquer des irritations cutanées. Un traitement régulier et adapté garantit une eau saine et cristalline.

**Chlore, brome ou oxygène actif ?** Le brome est recommandé pour les spas car il reste stable à haute température (35-40°C) et est moins irritant pour la peau et les yeux. Le chlore est économique mais nécessite des ajustements fréquents. L'oxygène actif convient aux peaux sensibles mais coûte plus cher.

**Programme d'entretien type** : Testez pH et désinfectant 2-3 fois par semaine. Maintenez le pH entre 7.2 et 7.6. Effectuez un traitement choc après chaque utilisation intensive. Changez l'eau tous les 3-4 mois.

**Notre sélection** : Produits HTH, Bayrol, AquaFinesse, compatibles tous spas.

📦 **Livraison France** 48-72h.
🛠 **Conseils personnalisés** pour établir votre programme d'entretien.`,
    relatedCategories: ['filtres'],
    internalLinks: ['/entretien-spa', '/pieces-accessoires']
  },
  // ACCESSOIRES
  'accessoires-spa': {
    title: 'Accessoires de spa',
    h1: 'Accessoires de spa – Livraison France',
    metaTitle: 'Accessoires de spa | Livraison France | Atlantique Spa Boutique',
    metaDescription: 'Large sélection d\'accessoires pour spa : lève-couverture, escalier, appuie-tête. Livraison rapide partout en France.',
    icon: '🛋️',
    brands: [],
    intro: `Nous proposons une sélection d'accessoires pour spa : lève-couverture, escalier, appuie-tête, couverture thermique. Livraison rapide partout en France. Besoin d'aide pour choisir ? Notre équipe vous conseille.`,
    seoText: `Les accessoires améliorent le confort et la praticité de votre spa au quotidien. Un lève-couverture facilite l'ouverture et prolonge la durée de vie de votre couverture. Un escalier sécurise l'accès. Des appuie-têtes ergonomiques optimisent la relaxation.

**Accessoires essentiels** : Lève-couverture (manuel ou assisté), escalier antidérapant, appuie-têtes ergonomiques, couverture thermique de remplacement, éclairage d'ambiance.

📦 **Livraison France** 48-72h.`,
    relatedCategories: ['filtres', 'produits-traitement'],
    internalLinks: ['/pieces-accessoires']
  }
};

// =============================================
// FAQ STANDARDISÉE PAR CATÉGORIE (3-4 questions min)
// Format: Comment choisir / Livraison France / Compatibilité / Installation
// =============================================
const CATEGORY_FAQ = {
  'pompes-circulation': [
    {
      question: 'Comment choisir une pompe de circulation pour mon spa ?',
      answer: 'Le choix dépend de la capacité de votre spa et du débit requis (exprimé en L/min). Les modèles JA50, JA75 et JA100 de LX Whirlpool couvrent 90% des spas. Vérifiez également le diamètre des raccords et la tension électrique. Envoyez-nous une photo de votre pompe actuelle pour un conseil personnalisé.'
    },
    {
      question: 'Livrez-vous partout en France ?',
      answer: 'Oui, nous livrons partout en France métropolitaine. Les pompes en stock sont expédiées sous 24-48h avec livraison sous 2-3 jours ouvrés. Frais de port offerts à partir de 100€ d\'achat.'
    },
    {
      question: 'Comment vérifier la compatibilité avant commande ?',
      answer: 'Envoyez-nous une photo de votre pompe actuelle avec les références visibles (étiquette). Nous vérifions gratuitement la compatibilité et vous confirmons le modèle adapté avant expédition. Aucun risque d\'erreur.'
    },
    {
      question: 'Proposez-vous des conseils pour l\'installation ?',
      answer: 'Oui, notre équipe vous accompagne par email pour l\'installation de vos pièces. Nous fournissons des instructions détaillées et répondons à toutes vos questions techniques.'
    }
  ],
  'pompes-massage': [
    {
      question: 'Comment choisir une pompe de massage pour mon spa ?',
      answer: 'La puissance dépend du nombre de jets : 2HP pour 4-6 jets, 3HP pour 6-10 jets, 4HP+ pour les spas équipés de nombreux jets. Vérifiez aussi le type (mono ou bi-vitesse) et le frame (48mm ou 56mm). Envoyez-nous une photo pour conseil personnalisé.'
    },
    {
      question: 'Livrez-vous partout en France ?',
      answer: 'Oui, livraison France métropolitaine sous 48-72h pour les produits en stock. Emballage renforcé pour protéger votre pompe pendant le transport.'
    },
    {
      question: 'Comment vérifier la compatibilité avant commande ?',
      answer: 'Photographiez l\'étiquette de votre pompe actuelle et envoyez-la nous. Nous vérifions les dimensions, la puissance et les raccords pour vous garantir une compatibilité parfaite.'
    },
    {
      question: 'Proposez-vous des conseils pour l\'installation ?',
      answer: 'Oui, notre équipe technique vous accompagne par email. Le remplacement d\'une pompe nécessite quelques compétences en plomberie. Nous fournissons des instructions détaillées avec chaque commande.'
    }
  ],
  'blower': [
    {
      question: 'Comment choisir un blower pour mon spa ?',
      answer: 'La puissance dépend du nombre de buses à air : 400W pour 6-10 buses, 700W pour 10-20 buses. Vérifiez également le diamètre de sortie d\'air (32mm ou 50mm) et si vous souhaitez un réchauffeur intégré.'
    },
    {
      question: 'Livrez-vous partout en France ?',
      answer: 'Oui, livraison France métropolitaine sous 48-72h. Emballage soigné pour garantir l\'intégrité du produit.'
    },
    {
      question: 'Comment vérifier la compatibilité avant commande ?',
      answer: 'Envoyez-nous une photo de votre blower actuel avec les références. Nous vérifions la puissance, le voltage et les dimensions avant de confirmer la compatibilité.'
    },
    {
      question: 'Proposez-vous des conseils pour l\'installation ?',
      answer: 'Oui, notre équipe vous guide par email. Le remplacement d\'un blower nécessite des compétences en électricité. Nous recommandons de faire appel à un électricien qualifié.'
    }
  ],
  'filtres': [
    {
      question: 'Comment choisir le bon filtre pour mon spa ?',
      answer: 'Mesurez votre cartouche actuelle : diamètre extérieur (en mm), longueur totale, et diamètre du trou central. Ces trois mesures déterminent la compatibilité. Envoyez-nous une photo avec un mètre pour vérification.'
    },
    {
      question: 'Livrez-vous partout en France ?',
      answer: 'Oui, livraison France métropolitaine sous 48-72h. Les filtres sont légers et bien protégés pour le transport.'
    },
    {
      question: 'Comment vérifier la compatibilité avant commande ?',
      answer: 'Envoyez-nous les dimensions de votre filtre actuel ou une photo avec les mesures. Nous confirmons le modèle compatible avant expédition. Service gratuit.'
    },
    {
      question: 'À quelle fréquence changer le filtre ?',
      answer: 'Nettoyez le filtre toutes les 2 semaines. Effectuez un trempage dégraissant mensuel. Remplacez la cartouche tous les 12-18 mois selon l\'utilisation pour maintenir une filtration optimale.'
    }
  ],
  'produits-traitement': [
    {
      question: 'Comment choisir le traitement adapté à mon spa ?',
      answer: 'Le brome est recommandé pour les spas (stable à haute température, moins irritant). Le chlore est économique mais moins stable. L\'oxygène actif convient aux peaux sensibles. Contactez-nous pour un conseil personnalisé selon votre fréquence d\'utilisation.'
    },
    {
      question: 'Livrez-vous partout en France ?',
      answer: 'Oui, livraison France métropolitaine sous 48-72h. Certains produits chimiques peuvent nécessiter un transporteur spécialisé.'
    },
    {
      question: 'Comment bien entretenir l\'eau de mon spa ?',
      answer: 'Testez pH et désinfectant 2-3 fois par semaine. Maintenez le pH entre 7.2 et 7.6. Faites un traitement choc après chaque utilisation intensive. Changez l\'eau tous les 3-4 mois.'
    },
    {
      question: 'Proposez-vous des conseils personnalisés ?',
      answer: 'Oui, contactez-nous pour établir un programme d\'entretien adapté à votre spa, votre eau locale et votre fréquence d\'utilisation. Conseil gratuit.'
    }
  ],
  'accessoires-spa': [
    {
      question: 'Comment choisir un lève-couverture ?',
      answer: 'Mesurez la largeur de votre spa et l\'espace disponible derrière. Les lève-couvertures manuels conviennent pour les spas jusqu\'à 2m20. Au-delà, optez pour un modèle assisté par vérin à gaz.'
    },
    {
      question: 'Livrez-vous partout en France ?',
      answer: 'Oui, livraison France métropolitaine. Les accessoires volumineux (escaliers, lève-couvertures) sont livrés par transporteur spécialisé.'
    },
    {
      question: 'Comment vérifier la compatibilité ?',
      answer: 'Pour les accessoires universels (escaliers, appuie-têtes), vérifiez les dimensions. Pour les pièces spécifiques, envoyez-nous une photo de votre spa avec ses mesures.'
    },
    {
      question: 'Proposez-vous des conseils pour la mise en place ?',
      answer: 'Oui, notre équipe vous accompagne par email. La plupart des accessoires (escaliers, lève-couvertures) sont faciles à installer sans intervention professionnelle.'
    }
  ]
};

// Default FAQ si catégorie non trouvée
const DEFAULT_FAQ = [
  {
    question: 'Comment vérifier la compatibilité d\'une pièce ?',
    answer: 'Envoyez-nous une photo de la pièce à remplacer avec les références visibles. Nous vérifions gratuitement la compatibilité et vous confirmons le modèle adapté avant expédition.'
  },
  {
    question: 'Livrez-vous partout en France ?',
    answer: 'Oui, livraison France métropolitaine sous 48-72h pour les produits en stock.'
  },
  {
    question: 'Proposez-vous des conseils pour l\'installation ?',
    answer: 'Oui, notre équipe technique vous accompagne par email. Nous fournissons des instructions détaillées avec chaque commande.'
  }
];

// =============================================
// COMPOSANT PRINCIPAL - PAGE CATÉGORIE
// =============================================
export default function PartsCategoryPage() {
  const { subcategory } = useParams();
  const location = useLocation();
  const [parts, setParts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [categories, setCategories] = useState({});
  const [openFaq, setOpenFaq] = useState(null);

  const pathParts = location.pathname.split('/').filter(Boolean);
  const category = pathParts[0];

  // Get SEO config for current category
  const seoConfig = subcategory ? CATEGORY_SEO_CONFIG[subcategory] : null;
  const currentFaq = subcategory && CATEGORY_FAQ[subcategory] ? CATEGORY_FAQ[subcategory] : DEFAULT_FAQ;

  useEffect(() => {
    fetchParts();
    fetchCategories();
  }, [category, subcategory]);

  const fetchParts = async () => {
    try {
      let url = `${API_URL}/api/parts?`;
      if (category) url += `main_category=${category}&`;
      if (subcategory) url += `sub_category=${subcategory}`;
      
      const res = await fetch(url);
      const data = await res.json();
      setParts(data);
    } catch (err) {
      console.error('Erreur chargement pièces:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchCategories = async () => {
    try {
      const res = await fetch(`${API_URL}/api/parts/categories`);
      const data = await res.json();
      setCategories(data);
    } catch (err) {
      console.error('Erreur chargement catégories:', err);
    }
  };

  // Dynamic SEO values from template
  const pageH1 = seoConfig?.h1 || `${seoConfig?.title || 'Pièces détachées'} pour spa – Livraison France`;
  const metaTitle = seoConfig?.metaTitle || `${seoConfig?.title || 'Pièces détachées'} pour spa | Livraison France | Atlantique Spa Boutique`;
  const metaDescription = seoConfig?.metaDescription || `Large sélection de ${(seoConfig?.title || 'pièces').toLowerCase()} pour spa. Livraison rapide partout en France. Compatibilité vérifiée avant expédition.`;

  // Breadcrumbs
  const breadcrumbs = [
    { name: 'Accueil', url: '/' },
    { name: 'Pièces & Accessoires', url: '/pieces-accessoires' },
    { name: category === 'pieces-detachees' ? 'Pièces détachées' : 'Accessoires', url: `/${category}` }
  ];
  if (subcategory && seoConfig) {
    breadcrumbs.push({ name: seoConfig.title, url: `/${category}/${subcategory}` });
  }

  // Generate FAQPage JSON-LD
  const faqJsonLd = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": currentFaq.map(faq => ({
      "@type": "Question",
      "name": faq.question,
      "acceptedAnswer": {
        "@type": "Answer",
        "text": faq.answer
      }
    }))
  };

  // Generate ItemList JSON-LD for products
  const itemListJsonLd = {
    "@context": "https://schema.org",
    "@type": "ItemList",
    "name": pageH1,
    "numberOfItems": parts.length,
    "itemListElement": parts.slice(0, 20).map((part, index) => ({
      "@type": "ListItem",
      "position": index + 1,
      "item": {
        "@type": "Product",
        "name": part.name,
        "sku": part.sku || part.slug,
        "brand": {
          "@type": "Brand",
          "name": part.brand || "Générique"
        },
        "image": part.images?.[0] || "",
        "url": `${window.location.origin}/${part.main_category}/${part.sub_category}/${part.slug}`,
        "offers": {
          "@type": "Offer",
          "price": part.price_ttc || part.price || 0,
          "priceCurrency": "EUR",
          "availability": part.availability === 'en_stock' ? "https://schema.org/InStock" : "https://schema.org/PreOrder",
          "seller": {
            "@type": "Organization",
            "name": "Atlantique Spa Boutique"
          }
        }
      }
    }))
  };

  // BreadcrumbList JSON-LD
  const breadcrumbJsonLd = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": breadcrumbs.map((bc, index) => ({
      "@type": "ListItem",
      "position": index + 1,
      "name": bc.name,
      "item": `${window.location.origin}${bc.url}`
    }))
  };

  return (
    <div className="min-h-screen bg-[#F8FAFC]">
      {/* SEO Meta Tags */}
      <SEO
        title={metaTitle}
        description={metaDescription}
        breadcrumbs={breadcrumbs}
      />

      {/* JSON-LD Structured Data */}
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumbJsonLd) }} />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(faqJsonLd) }} />
      {parts.length > 0 && (
        <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(itemListJsonLd) }} />
      )}

      {/* ============================================= */}
      {/* HERO SECTION - H1 SEO optimisé */}
      {/* ============================================= */}
      <div className="bg-gradient-to-r from-[#1E3A5F] to-[#2A4A70] text-white py-14">
        <div className="max-w-7xl mx-auto px-4">
          {/* Breadcrumb */}
          <nav className="flex items-center gap-2 text-sm text-white/70 mb-8 flex-wrap">
            {breadcrumbs.map((bc, index) => (
              <React.Fragment key={bc.url}>
                {index > 0 && <ChevronRight className="w-4 h-4" />}
                {index === breadcrumbs.length - 1 ? (
                  <span className="text-white">{bc.name}</span>
                ) : (
                  <Link to={bc.url} className="hover:text-white">{bc.name}</Link>
                )}
              </React.Fragment>
            ))}
          </nav>

          {/* H1 - Format obligatoire: [Nom] pour spa – Livraison France */}
          <h1 className="text-3xl md:text-4xl font-bold mb-5" style={{ fontFamily: 'Manrope, sans-serif' }}>
            {pageH1}
          </h1>
          
          {/* Badges Livraison France */}
          <div className="flex flex-wrap gap-3 mb-6">
            <span className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-sm px-4 py-2 rounded-lg text-sm font-medium">
              <Truck className="w-4 h-4" />
              📦 Livraison France métropolitaine 48-72h
            </span>
          </div>

          {/* Brand badges si disponibles */}
          {seoConfig?.brands && seoConfig.brands.length > 0 && (
            <div className="flex flex-wrap gap-2">
              {seoConfig.brands.map((brand) => (
                <span key={brand} className="bg-[#F97316]/20 text-orange-200 px-3 py-1.5 rounded-lg text-sm font-medium">
                  {brand}
                </span>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* ============================================= */}
      {/* BLOC INTRODUCTIF - Au-dessus du listing */}
      {/* ============================================= */}
      {seoConfig?.intro && (
        <div className="bg-white border-b border-[#E2E8F0]">
          <div className="max-w-7xl mx-auto px-4 py-8">
            <div className="flex items-start gap-4 max-w-4xl">
              <div className="w-12 h-12 bg-[#1E3A5F]/10 rounded-xl flex items-center justify-center flex-shrink-0">
                <CheckCircle className="w-6 h-6 text-[#1E3A5F]" />
              </div>
              <p className="text-[#475569] text-base leading-relaxed">
                {seoConfig.intro}
              </p>
            </div>
          </div>
        </div>
      )}

      <div className="max-w-7xl mx-auto px-4 py-10">
        <div className="flex flex-col lg:flex-row gap-10">
          {/* ============================================= */}
          {/* SIDEBAR - Navigation catégories */}
          {/* ============================================= */}
          <aside className="lg:w-72 flex-shrink-0">
            <div className="bg-white rounded-xl shadow-sm p-6 sticky top-24 border border-[#E2E8F0]">
              <h2 className="font-semibold text-lg mb-5 flex items-center gap-2 text-[#0F172A]">
                <Filter className="w-5 h-5 text-[#1E3A5F]" />
                Catégories
              </h2>
              
              {/* Pièces détachées */}
              <div className="mb-6">
                <Link 
                  to="/pieces-detachees" 
                  className="font-medium text-[#0F172A] hover:text-[#1E3A5F] flex items-center gap-2"
                >
                  <Wrench className="w-4 h-4" />
                  Pièces détachées
                </Link>
                <ul className="mt-3 space-y-2 pl-6">
                  {['pompes-circulation', 'pompes-massage', 'blower', 'filtres', 'claviers-commandes', 'rechauffeurs', 'boitiers-commande'].map((sub) => {
                    const info = CATEGORY_SEO_CONFIG[sub];
                    const count = categories['pieces-detachees']?.subcategories?.[sub] || 0;
                    return (
                      <li key={sub}>
                        <Link
                          to={`/pieces-detachees/${sub}`}
                          className={`text-base hover:text-[#1E3A5F] flex justify-between ${
                            subcategory === sub ? 'text-[#1E3A5F] font-semibold' : 'text-[#475569]'
                          }`}
                        >
                          <span>{info?.title || sub}</span>
                          {count > 0 && <span className="text-[#94A3B8]">({count})</span>}
                        </Link>
                      </li>
                    );
                  })}
                </ul>
              </div>

              {/* Accessoires & Entretien */}
              <div>
                <Link 
                  to="/entretien-spa" 
                  className="font-medium text-[#0F172A] hover:text-[#1E3A5F] flex items-center gap-2"
                >
                  <Package className="w-4 h-4" />
                  Entretien & Accessoires
                </Link>
                <ul className="mt-3 space-y-2 pl-6">
                  {['produits-traitement'].map((sub) => {
                    const info = CATEGORY_SEO_CONFIG[sub];
                    return (
                      <li key={sub}>
                        <Link
                          to={`/entretien-spa/${sub}`}
                          className={`text-base hover:text-[#1E3A5F] ${
                            subcategory === sub ? 'text-[#1E3A5F] font-semibold' : 'text-[#475569]'
                          }`}
                        >
                          {info?.title || sub}
                        </Link>
                      </li>
                    );
                  })}
                </ul>
              </div>

              {/* ============================================= */}
              {/* MAILLAGE INTERNE - Liens stratégiques */}
              {/* ============================================= */}
              {seoConfig?.relatedCategories && seoConfig.relatedCategories.length > 0 && (
                <div className="mt-6 pt-6 border-t border-[#E2E8F0]">
                  <p className="text-sm font-semibold text-[#64748B] mb-3">Voir aussi</p>
                  <ul className="space-y-2">
                    {seoConfig.relatedCategories.map((related) => {
                      const relatedInfo = CATEGORY_SEO_CONFIG[related];
                      return (
                        <li key={related}>
                          <Link
                            to={`/pieces-detachees/${related}`}
                            className="text-[#475569] hover:text-[#1E3A5F] text-sm flex items-center gap-1"
                          >
                            <ArrowRight className="w-3 h-3" />
                            {relatedInfo?.title || related}
                          </Link>
                        </li>
                      );
                    })}
                  </ul>
                </div>
              )}

              {seoConfig?.internalLinks && (
                <div className="mt-4">
                  {seoConfig.internalLinks.includes('/remplacement-pompe-spa-nantes') && (
                    <Link
                      to="/remplacement-pompe-spa-nantes"
                      className="flex items-center gap-2 text-[#F97316] hover:text-[#EA580C] font-semibold text-sm"
                    >
                      <ArrowRight className="w-4 h-4" />
                      Remplacement pompe Nantes
                    </Link>
                  )}
                </div>
              )}

              {/* Contact CTA */}
              <div className="mt-6 pt-6 border-t border-[#E2E8F0]">
                <p className="text-base text-[#475569] mb-4">Besoin d'aide pour choisir ?</p>
                <a
                  href="mailto:contact@atlantiquespaboutique.com"
                  className="flex items-center gap-2 text-[#1E3A5F] hover:text-[#152C4A] font-semibold text-base"
                >
                  <Mail className="w-4 h-4" />
                  Nous contacter
                </a>
              </div>
            </div>
          </aside>

          {/* ============================================= */}
          {/* MAIN CONTENT - Listing produits */}
          {/* ============================================= */}
          <main className="flex-1">
            {loading ? (
              <div className="flex justify-center py-16">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#1E3A5F]"></div>
              </div>
            ) : parts.length === 0 ? (
              <div className="text-center py-16 bg-white rounded-xl border border-[#E2E8F0]">
                <p className="text-[#475569] text-lg">Aucun produit trouvé dans cette catégorie.</p>
                <p className="text-[#64748B] text-sm mt-2">Contactez-nous pour vérifier la disponibilité.</p>
              </div>
            ) : (
              <>
                <p className="text-[#475569] mb-8 text-base">{parts.length} produit{parts.length > 1 ? 's' : ''} trouvé{parts.length > 1 ? 's' : ''}</p>
                
                {/* Product Grid */}
                <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-6">
                  {parts.map((part) => (
                    <PartCard key={part.slug} part={part} />
                  ))}
                </div>

                {/* ============================================= */}
                {/* TEXTE SEO LONG - Sous le listing (100-200 mots) */}
                {/* ============================================= */}
                {seoConfig?.seoText && (
                  <div className="mt-12 bg-white rounded-xl border border-[#E2E8F0] p-8" data-testid="seo-text-block">
                    <div className="prose prose-slate max-w-none">
                      {seoConfig.seoText.split('\n\n').map((paragraph, index) => (
                        <p key={index} className="text-[#475569] text-base leading-relaxed mb-4" 
                           dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(paragraph.replace(/\*\*(.*?)\*\*/g, '<strong class="text-[#0F172A]">$1</strong>')) }} 
                        />
                      ))}
                    </div>
                  </div>
                )}

                {/* ============================================= */}
                {/* BLOC CONFIANCE - National / Local */}
                {/* ============================================= */}
                <div className="mt-10 grid md:grid-cols-2 gap-6">
                  {/* National - Livraison France */}
                  <div className="bg-white rounded-xl border border-[#E2E8F0] p-6">
                    <div className="flex items-start gap-4">
                      <div className="w-12 h-12 bg-[#1E3A5F]/10 rounded-xl flex items-center justify-center flex-shrink-0">
                        <Truck className="w-6 h-6 text-[#1E3A5F]" />
                      </div>
                      <div>
                        <h3 className="font-semibold text-[#0F172A] text-lg mb-2">📦 Livraison France</h3>
                        <p className="text-[#475569] text-sm leading-relaxed">
                          Expédition sous 48-72h partout en France métropolitaine. Compatibilité vérifiée avant envoi.
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Conseil Expert */}
                  <div className="bg-[#1E3A5F] rounded-xl p-6 text-white">
                    <div className="flex items-start gap-4">
                      <div className="w-12 h-12 bg-white/10 rounded-xl flex items-center justify-center flex-shrink-0">
                        <Shield className="w-6 h-6 text-white" />
                      </div>
                      <div>
                        <h3 className="font-semibold text-lg mb-2">💬 Conseil expert</h3>
                        <p className="text-white/80 text-sm leading-relaxed">
                          Besoin d'aide pour choisir ? Contactez-nous avant commande pour toute question sur les dimensions et références.
                        </p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* CTA pièce spécifique */}
                <ContactCTA category={seoConfig?.title || 'Pièces détachées'} />

                {/* ============================================= */}
                {/* FAQ STANDARDISÉE - Avec JSON-LD */}
                {/* ============================================= */}
                <div className="mt-10" data-testid="category-faq">
                  <h2 className="text-2xl font-bold text-[#0F172A] mb-6" style={{ fontFamily: 'Manrope, sans-serif' }}>
                    FAQ – {seoConfig?.title || 'Pièces détachées spa'}
                  </h2>
                  <div className="space-y-3">
                    {currentFaq.map((faq, index) => (
                      <div key={index} className="bg-white rounded-xl border border-[#E2E8F0] overflow-hidden">
                        <button
                          onClick={() => setOpenFaq(openFaq === index ? null : index)}
                          className="w-full flex items-center justify-between p-5 text-left hover:bg-[#F8FAFC] transition-colors"
                        >
                          <span className="font-semibold text-[#0F172A] text-base pr-4">{faq.question}</span>
                          {openFaq === index ? (
                            <ChevronUp className="w-5 h-5 text-[#1E3A5F] flex-shrink-0" />
                          ) : (
                            <ChevronDown className="w-5 h-5 text-[#64748B] flex-shrink-0" />
                          )}
                        </button>
                        {openFaq === index && (
                          <div className="px-5 pb-5 text-[#475569] leading-relaxed text-base">
                            {faq.answer}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>

                {/* ============================================= */}
                {/* MAILLAGE INTERNE - Liens vers pages stratégiques */}
                {/* ============================================= */}
                <div className="mt-10 bg-[#F8FAFC] rounded-xl p-6 border border-[#E2E8F0]">
                  <h3 className="font-semibold text-[#0F172A] mb-4">Explorer notre catalogue</h3>
                  <div className="flex flex-wrap gap-3">
                    <Link to="/pieces-accessoires" className="text-[#1E3A5F] hover:text-[#152C4A] text-sm font-medium flex items-center gap-1">
                      <ArrowRight className="w-4 h-4" />
                      Toutes les pièces & accessoires
                    </Link>
                    {seoConfig?.internalLinks?.includes('/remplacement-pompe-spa-nantes') && (
                      <Link to="/remplacement-pompe-spa-nantes" className="text-[#F97316] hover:text-[#EA580C] text-sm font-medium flex items-center gap-1">
                        <ArrowRight className="w-4 h-4" />
                        Remplacement pompe à Nantes
                      </Link>
                    )}
                    {seoConfig?.relatedCategories?.map((related) => (
                      <Link key={related} to={`/pieces-detachees/${related}`} className="text-[#475569] hover:text-[#1E3A5F] text-sm flex items-center gap-1">
                        <ArrowRight className="w-4 h-4" />
                        {CATEGORY_SEO_CONFIG[related]?.title}
                      </Link>
                    ))}
                  </div>
                </div>
              </>
            )}
          </main>
        </div>
      </div>
    </div>
  );
}

// =============================================
// COMPOSANT CARTE PRODUIT
// =============================================
function PartCard({ part }) {
  const isPending = part.data_status?.includes('PENDING');
  const [addingToCart, setAddingToCart] = useState(false);
  
  const formatPrice = (price) => {
    if (!price || price === 0) return null;
    return new Intl.NumberFormat('fr-FR', {
      style: 'currency',
      currency: 'EUR',
      minimumFractionDigits: 2
    }).format(price).replace('€', '€ TTC');
  };

  const displayPrice = formatPrice(part.price_ttc || part.price);

  // Add to cart handler
  const handleAddToCart = (e) => {
    e.preventDefault();
    e.stopPropagation();
    
    const price = part.price_ttc || part.price;
    if (!price) {
      toast.error('Prix non disponible');
      return;
    }
    
    setAddingToCart(true);
    
    const cart = JSON.parse(localStorage.getItem('spa_cart') || '[]');
    const existingIndex = cart.findIndex(item => item.slug === part.slug);
    
    if (existingIndex >= 0) {
      cart[existingIndex].quantity += 1;
    } else {
      cart.push({
        slug: part.slug,
        name: part.name,
        price_ttc: price,
        image: part.images?.[0] || null,
        sku: part.sku,
        quantity: 1
      });
    }
    
    localStorage.setItem('spa_cart', JSON.stringify(cart));
    window.dispatchEvent(new Event('cartUpdated'));
    
    setTimeout(() => {
      setAddingToCart(false);
      
    }, 200);
  };
  
  return (
    <Link
      to={`/${part.main_category}/${part.sub_category}/${part.slug}`}
      className="bg-white rounded-xl border border-[#E2E8F0] hover:border-[#1E3A5F] hover:shadow-lg transition-all overflow-hidden group"
      data-testid={`part-card-${part.slug}`}
    >
      <div className="aspect-[4/3] bg-white flex items-center justify-center relative">
        {part.images?.length > 0 ? (
          <img src={part.images[0]} alt={part.name} className="h-full w-full object-contain p-3" />
        ) : (
          <span className="text-5xl opacity-40">
            {part.product_type === 'filtre' ? '🔍' : 
             part.product_type === 'piece_detachee' ? '⚙️' : '🧪'}
          </span>
        )}
        {part.availability === 'en_stock' && (
          <span className="absolute top-3 right-3 bg-[#10B981] text-white text-xs font-semibold px-2.5 py-1 rounded-lg">
            En stock
          </span>
        )}
      </div>

      <div className="p-5">
        {isPending && (
          <span className="inline-block px-3 py-1.5 text-xs font-medium bg-amber-100 text-amber-700 rounded-lg mb-3">
            Caractéristiques en validation
          </span>
        )}

        <p className="text-xs text-[#64748B] uppercase tracking-wide font-medium">{part.brand}</p>
        
        <h3 className="font-semibold text-[#0F172A] group-hover:text-[#1E3A5F] transition-colors line-clamp-2 mt-1 text-base">
          {part.name}
        </h3>

        {part.short_description && (
          <p className="text-sm text-[#475569] mt-2 line-clamp-2 leading-relaxed">{part.short_description}</p>
        )}

        {part.power_hp && (
          <div className="flex flex-wrap gap-2 mt-4 text-xs text-[#475569]">
            <span className="bg-[#F8FAFC] px-2.5 py-1.5 rounded-lg font-medium">{part.power_hp} HP</span>
            {part.flow_lpm && <span className="bg-[#F8FAFC] px-2.5 py-1.5 rounded-lg">{part.flow_lpm} L/min</span>}
            {part.speed_type && <span className="bg-[#F8FAFC] px-2.5 py-1.5 rounded-lg">{part.speed_type}</span>}
          </div>
        )}

        {part.diameter_mm && (
          <div className="flex gap-2 mt-4 text-xs text-[#475569]">
            <span className="bg-[#F8FAFC] px-2.5 py-1.5 rounded-lg">Ø {part.diameter_mm} mm</span>
            {part.length_mm && <span className="bg-[#F8FAFC] px-2.5 py-1.5 rounded-lg">L {part.length_mm} mm</span>}
          </div>
        )}

        <div className="mt-5 pt-4 border-t border-[#E2E8F0] flex justify-between items-center">
          {displayPrice ? (
            <span className="text-xl font-bold text-[#0F172A]" data-testid="product-price">
              {displayPrice}
            </span>
          ) : (
            <span className="text-[#64748B] text-sm italic">Prix sur demande</span>
          )}
          {displayPrice ? (
            <button
              onClick={handleAddToCart}
              disabled={addingToCart}
              className="flex items-center gap-2 bg-[#F97316] hover:bg-[#EA580C] text-white px-3 py-2 rounded-lg text-sm font-medium transition-all disabled:opacity-70"
              data-testid={`add-cart-${part.slug}`}
            >
              <ShoppingCart className="w-4 h-4" />
              {addingToCart ? '...' : 'Ajouter'}
            </button>
          ) : (
            <span className="text-sm text-[#1E3A5F] font-medium">Voir détails →</span>
          )}
        </div>
      </div>
    </Link>
  );
}
